import styled, { keyframes, css } from "styled-components";
import { 
  Search, X, Play, ChevronDown, Trash2, Filter, 
  SortAsc, SortDesc, Grid, List, AlertCircle 
} from "lucide-react";
import { useGames } from "../../../../Context/GameContext";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";

// Utility functions
const getImageWithFallback = (src) => {
  return src && typeof src === 'string' && src.trim() !== ''
    ? src
    : '/assets/images/default_card3.png';
};

// Enhanced GameImage component with lazy loading and error handling
const GameImageWithFallback = ({ src, alt, onImageLoad, ...props }) => {
  const [imgSrc, setImgSrc] = useState(getImageWithFallback(src));
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  const handleError = useCallback(() => {
    if (!hasError) {
      setHasError(true);
      setImgSrc("/assets/images/default_card.png");
      setIsLoading(false);
      onImageLoad && onImageLoad();
    }
  }, [hasError, onImageLoad]);

  const handleLoad = useCallback(() => {
    setIsLoading(false);
    onImageLoad && onImageLoad();
  }, [onImageLoad]);

  useEffect(() => {
    setImgSrc(getImageWithFallback(src));
    setHasError(false);
    setIsLoading(true);

    // Intersection Observer for lazy loading
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.src !== imgSrc) {
            img.src = imgSrc;
          }
          observer.unobserve(img);
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [src, imgSrc]);

  return (
    <>
      {isLoading && <SkeletonLoader />}
      <GameImage
        ref={imgRef}
        src={imgSrc}
        alt={alt}
        onError={handleError}
        onLoad={handleLoad}
        isLoading={isLoading}
        {...props}
      />
    </>
  );
};

// Animations
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spinAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulseAnimation = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.7; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: ${props => (props.isOpen ? "flex" : "none")};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const Container = styled.div`
  width: 100%;
  max-width: 1400px;
  max-height: 95vh;
  background: #292d2e;
  color: #E0E6E9;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  animation: ${slideIn} 0.3s ease-out;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    max-height: 100vh;
    border-radius: 0;
    margin: 0;
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, rgba(30, 35, 40, 0.8), rgba(20, 25, 30, 0.9));
  padding: 20px 24px;
  border-bottom: 1px solid rgba(224, 230, 233, 0.1);
  position: sticky;
  top: 0;
  z-index: 10;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #E0E6E9, #A0A6A9);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const CloseButton = styled.button`
  background: rgba(30, 35, 40, 0.5);
  border: none;
  color: #E0E6E9;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(37, 238, 137, 0.2);
    transform: rotate(90deg) scale(1.05);
  }
`;

const SearchAndFilters = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const SearchContainer = styled.div`
  flex: 1;
  position: relative;
  min-width: 300px;

  @media (max-width: 768px) {
    min-width: auto;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  background: rgba(50, 60, 70, 0.6);
  padding: 14px 16px 14px 48px;
  border: 0 solid rgba(224, 230, 233, 0.1);
  border-radius: 12px;
  color: #FFFFFF;
  font-size: 16px;
  font-weight: 400;
  transition: all 0.3s ease;

  &::placeholder {
    color: rgba(224, 230, 233, 0.5);
    font-weight: 400;
  }

  &:focus {
    outline: none;
    background: rgba(60, 70, 80, 0.8);
    border-color: #25ee89;
    box-shadow: 0 0 0 3px rgba(37, 238, 137, 0.1);
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(224, 230, 233, 0.6);
  pointer-events: none;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  background: ${props => props.active ? '#25ee89' : 'rgba(50, 60, 70, 0.6)'};
  border: 0 solid ${props => props.active ? '#25ee89' : 'rgba(224, 230, 233, 0.1)'};
  color: ${props => props.active ? '#292d2e' : '#E0E6E9'};
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;

  &:hover {
    background: ${props => props.active ? '#25ee89' : 'rgba(60, 70, 80, 0.8)'};
    transform: translateY(-1px);
  }
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownButton = styled(FilterButton)`
  min-width: 140px;
  justify-content: space-between;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: #292d2e;
  border: 1px solid rgba(224, 230, 233, 0.1);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  z-index: 1001;
  height: 22rem;
  overflow-y: auto;
  display: ${(props) => (props.isOpen ? "block" : "none")};
  animation: ${slideIn} 0.2s ease-out;
`;

const DropdownItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  color: #E0E6E9;
  font-size: 14px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background: rgba(37, 238, 137, 0.1);
    color: #25ee89;
  }

  &:first-child {
    border-radius: 12px 12px 0 0;
  }

  &:last-child {
    border-radius: 0 0 12px 12px;
  }
`;

const VendorDropdown = styled.div`
  position: relative;
  min-width: 200px;

  @media (max-width: 768px) {
    min-width: auto;
    width: 100%;
  }
`;

const VendorButton = styled(DropdownButton)`
  background: ${props => props.hasSelection ? '#25ee89' : 'rgba(50, 60, 70, 0.6)'};
  color: ${props => props.hasSelection ? '#292d2e' : '#E0E6E9'};
  border-color: ${props => props.hasSelection ? '#25ee89' : 'rgba(224, 230, 233, 0.1)'};
`;

const VendorMenu = styled(DropdownMenu)`
  width: 320px;
  left: auto;
  right: 0;

  @media (max-width: 768px) {
    width: 100%;
    left: 0;
    right: auto;
  }
`;

const VendorSearch = styled.div`
  padding: 16px;
  border-bottom: 1px solid rgba(224, 230, 233, 0.1);
  position: relative;
`;

const VendorSearchInput = styled.input`
  width: 100%;
  background: rgba(50, 60, 70, 0.6);
  padding: 10px 12px 10px 36px;
  border: 1px solid rgba(224, 230, 233, 0.2);
  border-radius: 8px;
  color: #FFFFFF;
  font-size: 14px;

  &::placeholder {
    color: rgba(224, 230, 233, 0.5);
  }

  &:focus {
    outline: none;
    border-color: #25ee89;
  }
`;

const VendorList = styled.div`
  max-height: 200px;
  overflow-y: auto;
`;

const VendorItem = styled.div`
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(37, 238, 137, 0.1);
  }
`;

const Checkbox = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${props => props.checked ? '#25ee89' : 'rgba(224, 230, 233, 0.3)'};
  border-radius: 4px;
  background: ${props => props.checked ? '#25ee89' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &::after {
    content: ${props => props.checked ? '"✓"' : '""'};
    color: #292d2e;
    font-size: 12px;
    font-weight: bold;
  }
`;

const VendorActions = styled.div`
  padding: 12px 16px;
  border-top: 1px solid rgba(224, 230, 233, 0.1);
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid rgba(224, 230, 233, 0.2);
  color: #E0E6E9;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(37, 238, 137, 0.1);
    border-color: #25ee89;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const StatsBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px;
  background: rgba(30, 35, 40, 0.3);
  border-radius: 12px;
  font-size: 14px;
  color: rgba(224, 230, 233, 0.8);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 8px;
`;

const ViewButton = styled.button`
  padding: 8px;
  background: ${props => props.active ? '#25ee89' : 'transparent'};
  border: 1px solid ${props => props.active ? '#25ee89' : 'rgba(224, 230, 233, 0.2)'};
  color: ${props => props.active ? '#292d2e' : '#E0E6E9'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? '#25ee89' : 'rgba(37, 238, 137, 0.1)'};
  }
`;

const GameGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.viewMode === 'grid' ? 'repeat(auto-fill, minmax(160px, 1fr))' : '1fr'};
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: ${props => props.viewMode === 'grid' ? 'repeat(auto-fill, minmax(140px, 1fr))' : '1fr'};
    gap: 12px;
  }
`;

const GameCard = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(30, 35, 40, 0.5);
  aspect-ratio: ${props => props.viewMode === 'grid' ? '3/4' : 'auto'};
  display: ${props => props.viewMode === 'list' ? 'flex' : 'block'};
  align-items: ${props => props.viewMode === 'list' ? 'center' : 'stretch'};
  gap: ${props => props.viewMode === 'list' ? '16px' : '0'};
  padding: ${props => props.viewMode === 'list' ? '16px' : '0'};
  min-height: ${props => props.viewMode === 'list' ? '80px' : 'auto'};

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(37, 238, 137, 0.15);
    
    .game-overlay {
      opacity: 1;
    }
  }
`;

const GameImage = styled.img`
  width: ${props => props.viewMode === 'list' ? '60px' : '100%'};
  height: ${props => props.viewMode === 'list' ? '60px' : '100%'};
  object-fit: cover;
  display: ${(props) => (props.isLoading ? "none" : "block")};
  border-radius: ${props => props.viewMode === 'list' ? '8px' : '0'};
`;

const GameInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const GameTitle = styled.h3`
  font-size: ${props => props.viewMode === 'list' ? '16px' : '14px'};
  font-weight: 600;
  margin: 0;
  color: #E0E6E9;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const GameMeta = styled.div`
  display: flex;
  gap: 8px;
  font-size: 12px;
  color: rgba(224, 230, 233, 0.6);
`;

const GameBadge = styled.span`
  background: rgba(37, 238, 137, 0.2);
  color: #25ee89;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
`;

const GameOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s ease;
  border-radius: 12px;
`;

const PlayButton = styled.div`
  width: 60px;
  height: 60px;
  background: #25EE89;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: scale(0.8);
  transition: all 0.3s ease;

  ${GameCard}:hover & {
    transform: scale(1);
  }
`;

const SkeletonLoader = styled.div`
  width: 100%;
  height: 100%;
  background: rgba(224, 230, 233, 0.1);
  border-radius: 12px;
  animation: ${pulseAnimation} 1.5s infinite ease-in-out;
  position: absolute;
  top: 0;
  left: 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 3px solid rgba(224, 230, 233, 0.1);
  border-radius: 50%;
  border-top-color: #25ee89;
  animation: ${spinAnimation} 0.8s linear infinite;
  margin-bottom: 20px;
`;

const LoadingText = styled.p`
  font-size: 16px;
  color: rgba(224, 230, 233, 0.8);
  margin: 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: rgba(224, 230, 233, 0.8);
`;

const EmptyIcon = styled(AlertCircle)`
  width: 64px;
  height: 64px;
  color: rgba(224, 230, 233, 0.4);
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #E0E6E9;
`;

const EmptyMessage = styled.p`
  font-size: 14px;
  margin: 0;
  max-width: 300px;
`;

const LoadMoreButton = styled.button`
  width: 100%;
  padding: 16px;
  margin-top: 24px;
  background: linear-gradient(135deg, #25ee89, #25ee89);
  border: none;
  border-radius: 12px;
  color: #292d2e;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: ${props => props.show ? 'block' : 'none'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(37, 238, 137, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorBanner = styled.div`

  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #F87171;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  animation: ${fadeIn} 0.3s ease-out;
`;

const DismissButton = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0;
  margin-left: auto;
`;

// Main Component
const GlobalSearchModal = ({ isOpen, onClose }) => {
  const {
    searchResults,
    totalCount,
    hasMorePages,
    isLoading,
    error,
    currentQuery,
    selectedVendors,
    sortBy,
    sortOrder,
    gameType,
    searchGames,
    loadMoreGames,
    handleGameLaunch,
    setSortBy,
    setSortOrder,
    setGameType,
    vendorDisplayNames,
    clearError
  } = useGames();

  // Local state
  const [searchInput, setSearchInput] = useState('');
  const [vendorSearchInput, setVendorSearchInput] = useState('');
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [imageLoadingStates, setImageLoadingStates] = useState({});

  // Refs
  const vendorDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Memoized vendor options
  const vendorOptions = useMemo(() => 
    Object.entries(vendorDisplayNames).map(([key, name]) => ({ key, name }))
  , [vendorDisplayNames]);

  const filteredVendors = useMemo(() => 
    vendorOptions.filter(vendor => 
      vendor.name.toLowerCase().includes(vendorSearchInput.toLowerCase())
    )
  , [vendorOptions, vendorSearchInput]);

  // Sort options
  const sortOptions = [
    { key: 'name', label: 'Name' },
    { key: 'vendor', label: 'Provider' },
    // { key: 'type', label: 'Game Type' }
  ];

  const gameTypeOptions = [
    { key: 'all', label: 'All Games' },
    // { key: 'slots', label: 'Slots' },
    // { key: 'table', label: 'Table Games' },
    // { key: 'live', label: 'Live Casino' }
  ];

  // Handle search input
  const handleSearchInput = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      searchGames(value, selectedVendors);
    }, 300);
  }, [searchGames, selectedVendors]);

  // Handle vendor selection
  const handleVendorToggle = useCallback((vendorKey) => {
    const newSelection = selectedVendors.includes(vendorKey)
      ? selectedVendors.filter(v => v !== vendorKey)
      : [...selectedVendors, vendorKey];
    
    searchGames(currentQuery, newSelection);
  }, [selectedVendors, currentQuery, searchGames]);

  const clearAllVendors = useCallback(() => {
    searchGames(currentQuery, []);
  }, [currentQuery, searchGames]);

  const selectAllVendors = useCallback(() => {
    searchGames(currentQuery, vendorOptions.map(v => v.key));
  }, [currentQuery, searchGames, vendorOptions]);

  // Handle image loading
  const handleImageLoad = useCallback((gameId) => {
    setImageLoadingStates(prev => ({
      ...prev,
      [gameId]: false
    }));
  }, []);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target)) {
        setShowVendorDropdown(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize image loading states
  useEffect(() => {
    const newStates = {};
    searchResults.forEach((game) => {
      const gameId = game.id || game.uuid || game.title;
      newStates[gameId] = imageLoadingStates[gameId] ?? true;
    });
    setImageLoadingStates(newStates);
  }, [searchResults]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  const renderContent = () => {
    if (isLoading && searchResults.length === 0) {
      return (
        <LoadingContainer>
          <Spinner />
          <LoadingText>Searching for games...</LoadingText>
        </LoadingContainer>
      );
    }

    if (searchResults.length === 0) {
      return (
        <EmptyState>
          <EmptyIcon />
          <EmptyTitle>No games found</EmptyTitle>
          <EmptyMessage>
            {currentQuery ? 
              `No games match "${currentQuery}". Try adjusting your search or filters.` :
              "Select providers or search for games to get started."
            }
          </EmptyMessage>
        </EmptyState>
      );
    }

    return (
      <>
        <StatsBar>
          <span>
            Showing {searchResults.length} of {totalCount} games
            {selectedVendors.length > 0 && ` from ${selectedVendors.length} provider(s)`}
          </span>
          <ViewToggle>
            <ViewButton 
              active={viewMode === 'grid'} 
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <Grid size={16} />
            </ViewButton>
            <ViewButton 
              active={viewMode === 'list'} 
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <List size={16} />
            </ViewButton>
          </ViewToggle>
        </StatsBar>

        <GameGrid viewMode={viewMode}>
          {searchResults.map((game) => {
            const gameId = game.id || game.uuid || game.title;
            const isImageLoading = imageLoadingStates[gameId] ?? true;

            return (
              <GameCard
                key={gameId}
                viewMode={viewMode}
                onClick={() => handleGameLaunch(game)}
              >
                {viewMode === 'grid' && isImageLoading && <SkeletonLoader />}
                
                <GameImageWithFallback
                  src={game.game_icon}
                  alt={game.title}
                  viewMode={viewMode}
                  onImageLoad={() => handleImageLoad(gameId)}
                />

                {viewMode === 'list' ? (
                  <GameInfo>
                    <GameTitle viewMode={viewMode}>{game.title}</GameTitle>
                    <GameMeta>
                      <GameBadge>{game.vendor}</GameBadge>
                      {game.game_type && <span>{game.game_type}</span>}
                    </GameMeta>
                  </GameInfo>
                ) : (
                  <GameOverlay className="game-overlay">
                    <PlayButton>
                      <Play size={24} color="#292d2e" fill="#292d2e" />
                    </PlayButton>
                  </GameOverlay>
                )}
              </GameCard>
            );
          })}
        </GameGrid>

        <LoadMoreButton
          show={hasMorePages}
          onClick={loadMoreGames}
          disabled={isLoading}
        >
          {isLoading ? 'Loading...' : 'Load More Games'}
        </LoadMoreButton>
      </>
    );
  };

  return (
    <ModalOverlay isOpen={isOpen}>
      <Container>
        <Header>
          <HeaderTop>
            <Title>Game Lib</Title>
            <CloseButton onClick={onClose}>
              <X size={20} />
            </CloseButton>
          </HeaderTop>

          <SearchAndFilters>
            <SearchContainer>
              <SearchIcon size={20} />
              <SearchInput
                type="text"
                placeholder="Search games..."
                value={searchInput}
                onChange={handleSearchInput}
              />
            </SearchContainer>

            <FilterContainer>
              <VendorDropdown ref={vendorDropdownRef}>
                <VendorButton
                  hasSelection={selectedVendors.length > 0}
                  onClick={() => setShowVendorDropdown(!showVendorDropdown)}
                >
                  <span>
                    {selectedVendors.length === 0 
                      ? 'All Providers' 
                      : `${selectedVendors.length} Selected`
                    }
                  </span>
                  <ChevronDown size={16} />
                </VendorButton>

                <VendorMenu isOpen={showVendorDropdown}>
                  <VendorSearch>
                    <SearchIcon size={16} />
                    <VendorSearchInput
                      placeholder="Search providers..."
                      value={vendorSearchInput}
                      onChange={(e) => setVendorSearchInput(e.target.value)}
                    />
                  </VendorSearch>

                  <VendorList>
                    {filteredVendors.map(vendor => (
                      <VendorItem
                        key={vendor.key}
                        onClick={() => handleVendorToggle(vendor.key)}
                      >
                        <Checkbox checked={selectedVendors.includes(vendor.key)} />
                        <span>{vendor.name}</span>
                      </VendorItem>
                    ))}
                  </VendorList>

                  <VendorActions>
                    <ActionButton onClick={selectAllVendors}>
                      Select All
                    </ActionButton>
                    <ActionButton onClick={clearAllVendors}>
                      <Trash2 size={14} />
                      Clear
                    </ActionButton>
                  </VendorActions>
                </VendorMenu>
              </VendorDropdown>

              <DropdownContainer ref={sortDropdownRef}>
                <DropdownButton
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                >
                  <span>Sort by {sortOptions.find(opt => opt.key === sortBy)?.label}</span>
                  {sortOrder === 'asc' ? <SortAsc size={16} /> : <SortDesc size={16} />}
                </DropdownButton>

                <DropdownMenu isOpen={showSortDropdown}>
                  {sortOptions.map(option => (
                    <DropdownItem
                      key={option.key}
                      onClick={() => {
                        setSortBy(option.key);
                        setShowSortDropdown(false);
                      }}
                    >
                      {option.label}
                      {sortBy === option.key && '✓'}
                    </DropdownItem>
                  ))}
                  <DropdownItem
                    onClick={() => {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      setShowSortDropdown(false);
                    }}
                  >
                    {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                  </DropdownItem>
                </DropdownMenu>
              </DropdownContainer>

              {gameTypeOptions.map(option => (
                <FilterButton
                  key={option.key}
                  active={gameType === option.key}
                  onClick={() => setGameType(option.key)}
                >
                  {option.label}
                </FilterButton>
              ))}
            </FilterContainer>
          </SearchAndFilters>
        </Header>

        <Content>
          {error && (  
            <ErrorBanner>
              <AlertCircle size={16} />
              <span>{error}</span>
              <DismissButton onClick={clearError}>
                <X size={16} />
              </DismissButton>
            </ErrorBanner>
          )}

          {renderContent()}
        </Content>
      </Container>
    </ModalOverlay>
  );
};

export default GlobalSearchModal;